import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { listTriageQueue, scheduleTriageIntent } from '../../api/triage.js'
import { listAvailableSlots, listDoctors } from '../../api/bookings.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import TriageBadge from '../../components/TriageBadge.jsx'
import TriageSummaryCard from '../../components/TriageSummaryCard.jsx'
import {
  firstError,
  pngDayKey,
  pngDayToUtcRange,
  pngTime,
} from '../../utils/format.js'

/**
 * Triage-Ops queue (platform admin).
 *
 * Lists AI-triage booking intents that still need a human to schedule — Red and
 * any flagged intent that is NOT patient self-booked. Scheduling one creates a
 * real bookings.Booking for the patient and links the triage so the RAG grade +
 * clinician summary appear on the doctor's queue.
 */
export default function TriageQueuePage() {
  const queue = useQuery({
    queryKey: ['triage-queue'],
    queryFn: listTriageQueue,
    refetchInterval: 30000,
  })
  const [openId, setOpenId] = useState(null)

  const rows = queue.data || []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Triage queue"
          subtitle="AI-triage intents awaiting scheduling. Red appears first. Scheduling links the triage summary to the appointment."
        />
        {queue.error && <ErrorBanner error={queue.error} onRetry={() => queue.refetch()} />}
        {queue.isLoading && <div className="text-sm text-slate-400 py-3">Loading…</div>}
        {!queue.isLoading && rows.length === 0 && (
          <div className="text-sm text-slate-400 py-3">Nothing waiting to be scheduled.</div>
        )}
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.id} className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
                    {r.patient_name || 'Unknown patient'}
                    <TriageBadge severity={r.severity} />
                    {!r.has_patient_account && (
                      <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-2 py-0.5">
                        no patient account
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.booking_type}
                    {r.reason && <> · {r.reason}</>}
                    {r.captured_at && <> · {new Date(r.captured_at).toLocaleString()}</>}
                  </div>
                </div>
                {r.has_patient_account ? (
                  <button
                    onClick={() => setOpenId(openId === r.id ? null : r.id)}
                    className="shrink-0 text-xs font-semibold bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5"
                  >
                    {openId === r.id ? 'Close' : 'Schedule'}
                  </button>
                ) : (
                  <span className="shrink-0 text-[11px] text-slate-400 italic">register patient first</span>
                )}
              </div>

              {r.summary && (
                <details className="mt-2">
                  <summary className="cursor-pointer select-none list-none text-xs font-semibold text-slate-600 hover:text-slate-800">
                    View AI pre-consultation triage
                  </summary>
                  <TriageSummaryCard severity={r.severity} summary={r.summary} className="mt-2" />
                </details>
              )}

              {openId === r.id && r.has_patient_account && (
                <IntentScheduler intentId={r.id} onDone={() => { setOpenId(null); queue.refetch() }} />
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline scheduler: pick doctor -> date -> open slot -> schedule.
// ---------------------------------------------------------------------------
function IntentScheduler({ intentId, onDone }) {
  const qc = useQueryClient()
  const [doctorId, setDoctorId] = useState('')
  const today = pngDayKey()
  const [date, setDate] = useState(() => today)   // default: TODAY (PNG)

  const doctorsQ = useQuery({ queryKey: ['triage-doctors'], queryFn: () => listDoctors({}) })
  // Guard transient invalid/empty values from the native date picker:
  // pngDayToUtcRange() throws on a bad key, which would crash the page.
  const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
  const utcRange = useMemo(
    () => (isValidDate ? pngDayToUtcRange(date) : null),
    [date, isValidDate],
  )
  const slotsQ = useQuery({
    queryKey: ['triage-slots', doctorId, date],
    queryFn: () => listAvailableSlots({
      doctorId, dateFrom: utcRange.start, dateTo: utcRange.end, availableOnly: true,
    }),
    enabled: Boolean(doctorId && utcRange),
  })
  // Show only slots still in the future: for TODAY this drops times that have
  // already passed; for a future date every slot qualifies.
  const slots = useMemo(() => {
    const now = Date.now()
    return (slotsQ.data || [])
      .filter((sl) => new Date(sl.slot_at).getTime() >= now)
      .sort((a, b) => new Date(a.slot_at) - new Date(b.slot_at))
  }, [slotsQ.data])

  const schedule = useMutation({
    mutationFn: (slotId) => scheduleTriageIntent(intentId, slotId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['triage-slots', doctorId, date] })
      onDone()
    },
  })

  return (
    <div className="mt-2 border border-slate-200 rounded bg-slate-50 p-3 space-y-2">
      {schedule.error && <ErrorBanner error={schedule.error} />}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={doctorId}
          onChange={(e) => setDoctorId(e.target.value)}
          className="text-sm border border-slate-300 rounded px-2 py-1"
        >
          <option value="">Choose a doctor…</option>
          {(doctorsQ.data || []).map((d) => (
            <option key={d.id} value={d.id}>
              Dr {d.full_name || d.username}{d.specialty ? ` · ${d.specialty}` : ''}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={date}
          min={today}
          onChange={(e) => { if (e.target.value) setDate(e.target.value) }}
          className="text-sm border border-slate-300 rounded px-2 py-1"
        />
      </div>

      {doctorId && (
        <div>
          {slotsQ.isLoading && <div className="text-xs text-slate-400">Loading slots…</div>}
          {!slotsQ.isLoading && slots.length === 0 && (
            <div className="text-xs text-slate-400">No open slots remaining for that day. Try another date or doctor.</div>
          )}
          <div className="flex flex-wrap gap-2 mt-1">
            {slots.map((s) => (
              <button
                key={s.id}
                disabled={schedule.isPending}
                onClick={() => schedule.mutate(s.id)}
                className="text-xs border border-wantok-300 text-wantok-800 hover:bg-wantok-50 rounded px-2 py-1 disabled:opacity-60"
              >
                {pngTime(s.slot_at)}
              </button>
            ))}
          </div>
        </div>
      )}
      {schedule.isError && (
        <div className="text-xs text-rose-600">{firstError(schedule.error)}</div>
      )}
    </div>
  )
}
